import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { pm, hrms, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

/* ─── constants ─────────────────────────────────────────────────── */
const MEMBER_ROLES = ['project_manager','scrum_master','tech_lead','team_lead','member'];
const ADMIN_ROLES  = ['super_admin','hr_admin','hr_head','ceo','cfo','cto'];
const PROJECT_MODIFY_ROLES = ['ceo','cto','cfo','project_manager','team_lead'];
const MANAGE_MEMBER_ROLES = ['project_manager','scrum_master','tech_lead','team_lead'];
const PROJECT_STATUSES = ['planned','active','on_hold','completed','cancelled'];

const STATE_GROUPS = [
  { key:'backlog',   label:'Backlog',    color:'#6B7280' },
  { key:'unstarted', label:'Unstarted',  color:'#3B82F6' },
  { key:'started',   label:'Started',    color:'#F97316' },
  { key:'completed', label:'Completed',  color:'#16A34A' },
  { key:'cancelled', label:'Cancelled',  color:'#EF4444' },
];

const PRESET_COLORS = [
  '#6B7280','#3B82F6','#8B5CF6','#EC4899','#14B8A6',
  '#F59E0B','#22C55E','#EF4444','#F97316','#06B6D4',
];

const STATUS_META = {
  planned:   { color:'#3B82F6', bg:'#EFF6FF' },
  active:    { color:'#22C55E', bg:'#F0FDF4' },
  on_hold:   { color:'#F59E0B', bg:'#FFFBEB' },
  completed: { color:'#9CA3AF', bg:'#F3F4F6' },
  cancelled: { color:'#F43F5E', bg:'#FFF1F2' },
};

const ROLE_META = {
  project_manager: { label:'Project Manager', color:'#7C3AED', bg:'#F5F3FF' },
  scrum_master:    { label:'Scrum Master',    color:'#1D4ED8', bg:'#EFF6FF' },
  tech_lead:       { label:'Tech Lead',       color:'#0891B2', bg:'#ECFEFF' },
  team_lead:       { label:'Team Lead',       color:'#0D9488', bg:'#F0FDFA' },
  member:          { label:'Member',          color:'#374151', bg:'#F3F4F6' },
  pm:              { label:'PM',              color:'#7C3AED', bg:'#F5F3FF' }, // legacy
};

const ACCENTS   = ['#F97316','#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#22C55E','#EF4444'];
const accentFor = (name = '') => ACCENTS[(name.charCodeAt(0) || 0) % ACCENTS.length];
const initials  = (name = '') => name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '?';

/* ─── shared ui ──────────────────────────────────────────────────── */
const inputStyle = {
  padding:'0.45rem 0.65rem', border:'1px solid #E4E4E7', borderRadius:7,
  fontSize:'0.82rem', background:'#fff', boxSizing:'border-box', width:'100%', outline:'none',
};

function Field({ label, children }) {
  return (
    <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
      <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#6B7280',
                     textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</span>
      {children}
    </label>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
      {PRESET_COLORS.map(c => (
        <button key={c} type="button" onClick={() => onChange(c)}
          style={{ width:22, height:22, borderRadius:'50%', background:c, border:'none',
                   cursor:'pointer', padding:0, outline: value === c ? `2px solid ${c}` : 'none',
                   outlineOffset:2, boxShadow: value === c ? '0 0 0 1px #fff inset' : 'none' }} />
      ))}
      <input type="color" value={value} onChange={e => onChange(e.target.value)}
        title="Custom color"
        style={{ width:22, height:22, borderRadius:'50%', border:'1px solid #E4E4E7',
                 padding:0, cursor:'pointer', background:'none' }} />
    </div>
  );
}

/* ─── StateFormRow — inline create/edit row ──────────────────────── */
function StateFormRow({ initial, onSave, onCancel, isNew }) {
  const [name,  setName]  = useState(initial?.name  ?? '');
  const [color, setColor] = useState(initial?.color ?? '#6B7280');
  const [group, setGroup] = useState(initial?.group ?? 'unstarted');
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), color, group });
    setSaving(false);
  };

  return (
    <form onSubmit={submit}
      style={{ display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.625rem 0.875rem',
               background: isNew ? '#F9FAFB' : '#FFFBEB',
               border:`1px ${isNew ? 'dashed' : 'solid'} #E4E4E7`, borderRadius:9,
               flexWrap:'wrap' }}>
      <div style={{ width:14, height:14, borderRadius:'50%', background:color, flexShrink:0,
                    border:'1.5px solid rgba(0,0,0,0.15)' }} />
      <input value={name} onChange={e => setName(e.target.value)} placeholder="State name…" required
        style={{ ...inputStyle, width:160, flex:'0 0 auto' }} autoFocus={isNew} />
      <select value={group} onChange={e => setGroup(e.target.value)}
        style={{ ...inputStyle, width:'auto', flex:'0 0 auto' }}>
        {STATE_GROUPS.map(g => (
          <option key={g.key} value={g.key}>{g.label}</option>
        ))}
      </select>
      <ColorPicker value={color} onChange={setColor} />
      <div style={{ display:'flex', gap:'0.4rem', marginLeft:'auto' }}>
        <button type="submit" disabled={saving}
          style={{ padding:'0.35rem 0.875rem', border:'none', borderRadius:6,
                   background: saving ? '#9CA3AF' : theme.primary,
                   color:'#fff', cursor: saving ? 'not-allowed' : 'pointer',
                   fontSize:'0.78rem', fontWeight:700 }}>
          {saving ? '…' : (isNew ? 'Add' : 'Save')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}
            style={{ padding:'0.35rem 0.75rem', border:'1px solid #E4E4E7', borderRadius:6,
                     background:'#fff', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:'#374151' }}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

/* ─── LabelFormRow — inline create/edit row ─────────────────────── */
function LabelFormRow({ initial, onSave, onCancel, isNew }) {
  const [name,  setName]  = useState(initial?.name  ?? '');
  const [color, setColor] = useState(initial?.color ?? '#3B82F6');
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), color });
    setSaving(false);
  };

  return (
    <form onSubmit={submit}
      style={{ display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.625rem 0.875rem',
               background: isNew ? '#F9FAFB' : '#FFFBEB',
               border:`1px ${isNew ? 'dashed' : 'solid'} #E4E4E7`, borderRadius:9,
               flexWrap:'wrap' }}>
      <div style={{ width:14, height:14, borderRadius:4, background:color, flexShrink:0,
                    border:'1.5px solid rgba(0,0,0,0.15)' }} />
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Label name…" required
        style={{ ...inputStyle, width:180, flex:'0 0 auto' }} autoFocus={isNew} />
      <ColorPicker value={color} onChange={setColor} />
      <div style={{ display:'flex', gap:'0.4rem', marginLeft:'auto' }}>
        <button type="submit" disabled={saving}
          style={{ padding:'0.35rem 0.875rem', border:'none', borderRadius:6,
                   background: saving ? '#9CA3AF' : theme.primary,
                   color:'#fff', cursor: saving ? 'not-allowed' : 'pointer',
                   fontSize:'0.78rem', fontWeight:700 }}>
          {saving ? '…' : (isNew ? 'Add' : 'Save')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}
            style={{ padding:'0.35rem 0.75rem', border:'1px solid #E4E4E7', borderRadius:6,
                     background:'#fff', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:'#374151' }}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

/* ─── GeneralTab ─────────────────────────────────────────────────── */
function LogoUpload({ project, onRefresh, canModify }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const [preview, setPreview]     = useState(project.logo ? getFileUrl(project.logo) : null);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/png') { setError('Only PNG files are allowed.'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('File must be under 2 MB.'); return; }
    setError('');
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await pm.uploadProjectLogo(project.id, fd);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
      setPreview(project.logo ? getFileUrl(project.logo) : null);
    } finally { setUploading(false); }
  };

  return (
    <div style={{ borderBottom:'1px solid #E4E4E7', paddingBottom:'1.25rem', marginBottom:'1.25rem' }}>
      <p style={{ fontSize:'0.72rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.04em', margin:'0 0 0.75rem' }}>Project Logo</p>
      <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
        {/* Preview */}
        <div style={{ width:72, height:72, borderRadius:12, border:'1.5px solid #E4E4E7', overflow:'hidden', background:'#F9FAFB', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          {preview
            ? <img src={preview} alt="logo" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
            : <span style={{ fontSize:'1.6rem', color:'#D1D5DB' }}>🖼</span>
          }
        </div>
        <div>
          {canModify && (
            <>
              <button type="button" disabled={uploading}
                onClick={() => fileRef.current?.click()}
                style={{ padding:'0.4rem 1rem', background: uploading ? '#9CA3AF' : theme.primary, color:'#fff', border:'none', borderRadius:7, fontSize:'0.8rem', fontWeight:600, cursor: uploading ? 'not-allowed' : 'pointer' }}>
                {uploading ? 'Uploading…' : preview ? 'Change Logo' : 'Upload Logo'}
              </button>
              <input ref={fileRef} type="file" accept=".png,image/png" style={{ display:'none' }} onChange={handleFile} />
              <p style={{ margin:'0.35rem 0 0', fontSize:'0.72rem', color:'#9CA3AF' }}>PNG only · Max 2 MB</p>
            </>
          )}
          {error && <p style={{ margin:'0.35rem 0 0', fontSize:'0.75rem', color:'#DC2626' }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}

function GeneralTab({ project, onRefresh, user }) {
  const canModify = PROJECT_MODIFY_ROLES.includes(user?.role) ||
    (user?.emp_id != null && project?.manager_id != null &&
     Number(user.emp_id) === Number(project.manager_id));
  const [form, setForm] = useState({
    name:               project.name,
    description:        project.description || '',
    status:             project.status,
    start_date:         project.start_date ? project.start_date.split('T')[0] : '',
    end_date:           project.end_date   ? project.end_date.split('T')[0]   : '',
    total_value:        project.total_value ?? '',
    payment_percentage: project.payment_percentage ?? '',
    timeline_notes:     project.timeline_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError('');
    try {
      await pm.updateProject(project.id, {
        name:               form.name.trim(),
        description:        form.description.trim() || null,
        status:             form.status,
        start_date:         form.start_date || null,
        end_date:           form.end_date   || null,
        total_value:        form.total_value !== '' ? Number(form.total_value) : null,
        payment_percentage: form.payment_percentage !== '' ? Number(form.payment_percentage) : null,
        timeline_notes:     form.timeline_notes.trim() || null,
      });
      onRefresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sm = STATUS_META[form.status] || STATUS_META.active;

  return (
    <form onSubmit={save} style={{ display:'flex', flexDirection:'column', gap:'1rem', maxWidth:640 }}>
      <LogoUpload project={project} onRefresh={onRefresh} canModify={canModify} />
      {error && (
        <div style={{ background:'#FEE2E2', color:'#DC2626', padding:'0.5rem 0.75rem',
                      borderRadius:7, fontSize:'0.78rem' }}>{error}</div>
      )}
      <Field label="Project Name *">
        <input required value={form.name} onChange={set('name')}
               placeholder="Project name" style={inputStyle} />
      </Field>
      <Field label="Description">
        <textarea value={form.description} onChange={set('description')} rows={3}
                  placeholder="What is this project about?"
                  style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit' }} />
      </Field>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'0.75rem' }}>
        <Field label="Status">
          <select value={form.status} onChange={set('status')} style={inputStyle}>
            {PROJECT_STATUSES.map(s => (
              <option key={s} value={s}>
                {s.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Start Date">
          <input type="date" value={form.start_date} onChange={set('start_date')} style={inputStyle} />
        </Field>
        <Field label="End Date">
          <input type="date" value={form.end_date} onChange={set('end_date')} style={inputStyle} />
        </Field>
      </div>

      {/* Financial & Timeline fields */}
      <div style={{ borderTop:'1px solid #E4E4E7', paddingTop:'1rem', marginTop:'0.25rem' }}>
        <p style={{ fontSize:'0.72rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.04em', margin:'0 0 0.75rem' }}>Financial & Timeline</p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
          <Field label="Total Project Value (₹)">
            <input type="number" min="0" step="0.01" value={form.total_value} onChange={set('total_value')}
                   placeholder="e.g. 500000" style={inputStyle} />
          </Field>
          <Field label="Payment Received (%)">
            <input type="number" min="0" max="100" step="0.1" value={form.payment_percentage} onChange={set('payment_percentage')}
                   placeholder="0–100" style={inputStyle} />
          </Field>
        </div>
        <Field label="Timeline / Delivery Notes">
          <textarea value={form.timeline_notes} onChange={set('timeline_notes')} rows={2}
                    placeholder="Key milestones, phases or delivery schedule…"
                    style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit' }} />
        </Field>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:sm.bg,
                       color:sm.color, padding:'3px 12px', borderRadius:9999,
                       fontSize:'0.75rem', fontWeight:700 }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background:sm.color }} />
          {form.status.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}
        </span>
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem', marginLeft:'auto' }}>
          {saved && (
            <span style={{ fontSize:'0.78rem', color:'#166534', fontWeight:600,
                           background:'#F0FDF4', padding:'3px 10px', borderRadius:9999,
                           border:'1px solid #BBF7D0' }}>
              ✓ Saved
            </span>
          )}
          {canModify ? (
            <button type="submit" disabled={saving}
              style={{ padding:'0.45rem 1.25rem', background: saving ? '#9CA3AF' : theme.primary,
                       color:'#fff', border:'none', borderRadius:8,
                       cursor: saving ? 'not-allowed' : 'pointer',
                       fontWeight:700, fontSize:'0.82rem' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          ) : (
            <span style={{ fontSize:'0.78rem', color:'#9CA3AF', fontStyle:'italic' }}>
              View only — only CEO, CFO, CTO, Project Manager or Team Lead can edit
            </span>
          )}
        </div>
      </div>
    </form>
  );
}

/* ─── StatesTab ──────────────────────────────────────────────────── */
function StatesTab({ projectId }) {
  const [states,    setStates]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNew,   setShowNew]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    pm.getStates(projectId).then(r => setStates(r.data)).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const createState = async payload => {
    await pm.createState(projectId, payload).catch(e => { alert(e.response?.data?.detail || 'Failed'); throw e; });
    setShowNew(false);
    load();
  };

  const updateState = async (id, payload) => {
    await pm.updateState(id, payload).catch(e => { alert(e.response?.data?.detail || 'Failed'); throw e; });
    setEditingId(null);
    load();
  };

  const deleteState = async (s) => {
    if (!window.confirm(`Delete state "${s.name}"?\nIssues in this state will be moved to the default state.`)) return;
    await pm.deleteState(s.id).catch(e => alert(e.response?.data?.detail || 'Failed'));
    load();
  };

  const setDefault = async (s) => {
    await pm.updateState(s.id, { is_default: true }).catch(e => alert(e.response?.data?.detail || 'Failed'));
    load();
  };

  if (loading) return (
    <div style={{ padding:'2rem', textAlign:'center', color:'#9CA3AF', fontSize:'0.82rem' }}>Loading states…</div>
  );

  /* group states by their group key */
  const grouped = STATE_GROUPS.reduce((acc, g) => {
    acc[g.key] = states.filter(s => s.group === g.key).sort((a,b) => (a.sequence||0)-(b.sequence||0));
    return acc;
  }, {});

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem', maxWidth:700 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <p style={{ margin:0, fontSize:'0.82rem', color:'#6B7280' }}>
          Define workflow states for issues in this project.
        </p>
        <button onClick={() => { setShowNew(true); setEditingId(null); }}
          style={{ padding:'0.38rem 0.875rem', border:'none', borderRadius:7,
                   background:theme.primary, color:'#fff', cursor:'pointer',
                   fontSize:'0.78rem', fontWeight:700 }}>
          + New State
        </button>
      </div>

      {showNew && (
        <StateFormRow isNew
          onSave={createState}
          onCancel={() => setShowNew(false)} />
      )}

      {STATE_GROUPS.map(g => {
        const items = grouped[g.key] || [];
        if (!items.length) return null;
        return (
          <div key={g.key}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem',
                          marginBottom:'0.5rem', padding:'0 0.25rem' }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:g.color, flexShrink:0 }} />
              <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151' }}>{g.label}</span>
              <span style={{ fontSize:'0.7rem', color:'#9CA3AF' }}>({items.length})</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
              {items.map(s => editingId === s.id ? (
                <StateFormRow key={s.id} initial={s}
                  onSave={p => updateState(s.id, p)}
                  onCancel={() => setEditingId(null)} />
              ) : (
                <div key={s.id}
                  style={{ display:'flex', alignItems:'center', gap:'0.625rem',
                           padding:'0.5rem 0.875rem', background:'#fff',
                           border:'1px solid #E4E4E7', borderRadius:9,
                           borderLeft:`3px solid ${s.color || g.color}` }}>
                  <div style={{ width:12, height:12, borderRadius:'50%', background:s.color || g.color,
                                flexShrink:0, border:'1.5px solid rgba(0,0,0,0.1)' }} />
                  <span style={{ flex:1, fontSize:'0.85rem', fontWeight:600, color:'#111827' }}>
                    {s.name}
                  </span>
                  {s.is_default && (
                    <span style={{ fontSize:'0.67rem', fontWeight:700, color:'#059669',
                                   background:'#ECFDF5', padding:'1px 7px', borderRadius:4, border:'1px solid #A7F3D0' }}>
                      Default
                    </span>
                  )}
                  <div style={{ display:'flex', gap:'0.3rem' }}>
                    {!s.is_default && (
                      <button onClick={() => setDefault(s)} title="Set as default"
                        style={{ background:'none', border:'1px solid #E4E4E7', borderRadius:5,
                                 padding:'2px 7px', cursor:'pointer', fontSize:'0.7rem', color:'#6B7280', fontWeight:600 }}
                        onMouseEnter={e => e.currentTarget.style.borderColor='#059669'}
                        onMouseLeave={e => e.currentTarget.style.borderColor='#E4E4E7'}>
                        Set Default
                      </button>
                    )}
                    <button onClick={() => { setEditingId(s.id); setShowNew(false); }}
                      style={{ background:'none', border:'1px solid #E4E4E7', borderRadius:5,
                               padding:'2px 7px', cursor:'pointer', fontSize:'0.7rem', color:'#6B7280', fontWeight:600 }}
                      onMouseEnter={e => e.currentTarget.style.color='#374151'}
                      onMouseLeave={e => e.currentTarget.style.color='#6B7280'}>
                      Edit
                    </button>
                    <button onClick={() => deleteState(s)}
                      style={{ background:'none', border:'1px solid #E4E4E7', borderRadius:5,
                               padding:'2px 7px', cursor:'pointer', fontSize:'0.7rem', color:'#9CA3AF', fontWeight:600 }}
                      onMouseEnter={e => { e.currentTarget.style.color='#EF4444'; e.currentTarget.style.borderColor='#FCA5A5'; }}
                      onMouseLeave={e => { e.currentTarget.style.color='#9CA3AF'; e.currentTarget.style.borderColor='#E4E4E7'; }}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {states.length === 0 && !showNew && (
        <div style={{ textAlign:'center', padding:'3rem', color:'#9CA3AF' }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.5rem', opacity:0.4 }}>◉</div>
          <p style={{ fontSize:'0.85rem', fontWeight:600, color:'#374151', margin:0 }}>No states yet</p>
          <p style={{ fontSize:'0.78rem', margin:'0.25rem 0 1rem' }}>Add workflow states to track issue progress.</p>
          <button onClick={() => setShowNew(true)}
            style={{ padding:'0.45rem 1rem', border:'none', borderRadius:7,
                     background:theme.primary, color:'#fff', cursor:'pointer',
                     fontSize:'0.78rem', fontWeight:700 }}>
            Add First State
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── LabelsTab ──────────────────────────────────────────────────── */
function LabelsTab({ projectId }) {
  const [labels,    setLabels]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNew,   setShowNew]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    pm.getLabels(projectId).then(r => setLabels(r.data)).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const createLabel = async payload => {
    await pm.createLabel(projectId, payload).catch(e => { alert(e.response?.data?.detail || 'Failed'); throw e; });
    setShowNew(false);
    load();
  };

  const updateLabel = async (id, payload) => {
    await pm.updateLabel(id, payload).catch(e => { alert(e.response?.data?.detail || 'Failed'); throw e; });
    setEditingId(null);
    load();
  };

  const deleteLabel = async (l) => {
    if (!window.confirm(`Delete label "${l.name}"?`)) return;
    await pm.deleteLabel(l.id).catch(e => alert(e.response?.data?.detail || 'Failed'));
    load();
  };

  if (loading) return (
    <div style={{ padding:'2rem', textAlign:'center', color:'#9CA3AF', fontSize:'0.82rem' }}>Loading labels…</div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', maxWidth:600 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.25rem' }}>
        <p style={{ margin:0, fontSize:'0.82rem', color:'#6B7280' }}>
          Labels help categorise and filter issues.
        </p>
        <button onClick={() => { setShowNew(true); setEditingId(null); }}
          style={{ padding:'0.38rem 0.875rem', border:'none', borderRadius:7,
                   background:theme.primary, color:'#fff', cursor:'pointer',
                   fontSize:'0.78rem', fontWeight:700 }}>
          + New Label
        </button>
      </div>

      {showNew && (
        <LabelFormRow isNew
          onSave={createLabel}
          onCancel={() => setShowNew(false)} />
      )}

      {labels.map(l => editingId === l.id ? (
        <LabelFormRow key={l.id} initial={l}
          onSave={p => updateLabel(l.id, p)}
          onCancel={() => setEditingId(null)} />
      ) : (
        <div key={l.id}
          style={{ display:'flex', alignItems:'center', gap:'0.625rem',
                   padding:'0.5rem 0.875rem', background:'#fff',
                   border:'1px solid #E4E4E7', borderRadius:9 }}>
          <div style={{ width:12, height:12, borderRadius:3, background:l.color || '#6B7280',
                        flexShrink:0, border:'1.5px solid rgba(0,0,0,0.1)' }} />
          <span style={{ flex:1, fontSize:'0.85rem', fontWeight:600, color:'#111827' }}>
            {l.name}
          </span>
          {/* preview pill */}
          <span style={{ fontSize:'0.67rem', fontWeight:600, padding:'1px 8px', borderRadius:9999,
                         background:`${l.color}18`, color:l.color, border:`1px solid ${l.color}30` }}>
            {l.name}
          </span>
          <div style={{ display:'flex', gap:'0.3rem' }}>
            <button onClick={() => { setEditingId(l.id); setShowNew(false); }}
              style={{ background:'none', border:'1px solid #E4E4E7', borderRadius:5,
                       padding:'2px 7px', cursor:'pointer', fontSize:'0.7rem', color:'#6B7280', fontWeight:600 }}
              onMouseEnter={e => e.currentTarget.style.color='#374151'}
              onMouseLeave={e => e.currentTarget.style.color='#6B7280'}>
              Edit
            </button>
            <button onClick={() => deleteLabel(l)}
              style={{ background:'none', border:'1px solid #E4E4E7', borderRadius:5,
                       padding:'2px 7px', cursor:'pointer', fontSize:'0.7rem', color:'#9CA3AF', fontWeight:600 }}
              onMouseEnter={e => { e.currentTarget.style.color='#EF4444'; e.currentTarget.style.borderColor='#FCA5A5'; }}
              onMouseLeave={e => { e.currentTarget.style.color='#9CA3AF'; e.currentTarget.style.borderColor='#E4E4E7'; }}>
              Delete
            </button>
          </div>
        </div>
      ))}

      {labels.length === 0 && !showNew && (
        <div style={{ textAlign:'center', padding:'3rem', color:'#9CA3AF' }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.5rem', opacity:0.4 }}>🏷</div>
          <p style={{ fontSize:'0.85rem', fontWeight:600, color:'#374151', margin:0 }}>No labels yet</p>
          <p style={{ fontSize:'0.78rem', margin:'0.25rem 0 1rem' }}>Labels help you filter and categorise issues.</p>
          <button onClick={() => setShowNew(true)}
            style={{ padding:'0.45rem 1rem', border:'none', borderRadius:7,
                     background:theme.primary, color:'#fff', cursor:'pointer',
                     fontSize:'0.78rem', fontWeight:700 }}>
            Add First Label
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── EmployeeSearch — searchable combobox ───────────────────────── */
function EmployeeSearch({ employees, value, onChange }) {
  const [query,  setQuery]  = useState('');
  const [open,   setOpen]   = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);

  const selected = employees.find(e => e.id === value);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return employees.slice(0, 50);
    return employees.filter(e =>
      e.full_name?.toLowerCase().includes(q) ||
      e.emp_code?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [query, employees]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (emp) => {
    onChange(emp.id);
    setQuery('');
    setOpen(false);
    setActive(-1);
  };

  const clear = () => { onChange(''); setQuery(''); setOpen(false); };

  const handleKey = (e) => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true); return; }
    if (e.key === 'ArrowDown')  { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')    { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    if (e.key === 'Enter')      { e.preventDefault(); if (active >= 0 && filtered[active]) select(filtered[active]); }
    if (e.key === 'Escape')     { setOpen(false); setActive(-1); }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        border: `1px solid ${open ? '#6366F1' : '#E4E4E7'}`,
        borderRadius: 7, background: '#fff', overflow: 'hidden',
        boxShadow: open ? '0 0 0 2px #6366F120' : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}>
        {selected && !open ? (
          <div style={{ flex: 1, padding: '0.45rem 0.65rem', fontSize: '0.82rem', color: '#111827',
                        display: 'flex', alignItems: 'center', gap: '0.5rem', overflow: 'hidden' }}>
            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected.full_name}
            </span>
            <span style={{ fontSize: '0.72rem', color: '#9CA3AF', fontFamily: 'monospace', flexShrink: 0 }}>
              {selected.emp_code}
            </span>
          </div>
        ) : (
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); setActive(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKey}
            placeholder="Search by name or employee code…"
            style={{ flex: 1, padding: '0.45rem 0.65rem', border: 'none', outline: 'none',
                     fontSize: '0.82rem', background: 'transparent', color: '#111827', minWidth: 0 }}
          />
        )}
        {selected
          ? <button type="button" onClick={clear}
              style={{ padding: '0 0.5rem', border: 'none', background: 'none', cursor: 'pointer',
                       color: '#9CA3AF', fontSize: '1rem', lineHeight: 1, flexShrink: 0 }}
              title="Clear">×</button>
          : <span style={{ padding: '0 0.5rem', color: '#9CA3AF', fontSize: '0.75rem', flexShrink: 0,
                           pointerEvents: 'none' }}>▾</span>
        }
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1px solid #E4E4E7', borderRadius: 8,
          boxShadow: '0 6px 24px rgba(0,0,0,0.1)', maxHeight: 240, overflowY: 'auto',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9CA3AF', textAlign: 'center' }}>
              No employees found
            </div>
          ) : filtered.map((emp, i) => (
            <div key={emp.id}
              onMouseDown={() => select(emp)}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: '0.5rem 0.875rem', cursor: 'pointer', fontSize: '0.82rem',
                display: 'flex', alignItems: 'center', gap: '0.625rem',
                background: i === active ? '#F5F3FF' : 'transparent',
                borderBottom: i < filtered.length - 1 ? '1px solid #F3F4F6' : 'none',
              }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: `${ACCENTS[(emp.full_name?.charCodeAt(0) || 0) % ACCENTS.length]}18`,
                color: ACCENTS[(emp.full_name?.charCodeAt(0) || 0) % ACCENTS.length],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '0.68rem',
              }}>
                {initials(emp.full_name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {emp.full_name}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#9CA3AF', fontFamily: 'monospace' }}>
                  {emp.emp_code}{emp.department ? ` · ${emp.department}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── MembersTab ─────────────────────────────────────────────────── */
function MembersTab({ projectId, project, user }) {
  const [members,   setMembers]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [newEmpId,  setNewEmpId]  = useState('');
  const [newRole,   setNewRole]   = useState('member');

  const canManage = useMemo(() => {
    if (ADMIN_ROLES.includes(user?.role)) return true;
    // Use Number() to avoid type mismatch between JSON integer and auth context value
    if (project?.manager_id != null && user?.emp_id != null &&
        Number(project.manager_id) === Number(user.emp_id)) return true;
    const myMembership = members.find(
      m => m.employee_id != null && user?.emp_id != null &&
           Number(m.employee_id) === Number(user.emp_id)
    );
    return myMembership ? MANAGE_MEMBER_ROLES.includes(myMembership.role) : false;
  }, [user, project, members]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      pm.getMembers(projectId),
      hrms.getEmployees({ employment_status:'active' }),
    ]).then(([mRes, eRes]) => {
      setMembers(mRes.data  || []);
      setEmployees(eRes.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const addMember = async e => {
    e.preventDefault();
    if (!newEmpId) return;
    await pm.addMember(projectId, { employee_id: Number(newEmpId), role: newRole })
      .catch(err => alert(err.response?.data?.detail || 'Failed'));
    setNewEmpId('');
    load();
  };

  const updateRole = async (m, role) => {
    await pm.updateMember(projectId, m.id, { role })
      .catch(err => alert(err.response?.data?.detail || 'Failed'));
    load();
  };

  const removeMember = async m => {
    const name = m.employee_name || m.name || 'this member';
    if (!window.confirm(`Remove ${name} from this project?`)) return;
    await pm.removeMember(projectId, m.id)
      .catch(err => alert(err.response?.data?.detail || 'Failed'));
    load();
  };

  if (loading) return (
    <div style={{ padding:'2rem', textAlign:'center', color:'#9CA3AF', fontSize:'0.82rem' }}>Loading members…</div>
  );

  const existingIds  = new Set(members.map(m => m.employee_id));
  const availableEmps = employees.filter(e => !existingIds.has(e.id));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', maxWidth:700 }}>
      {/* Member list */}
      {members.map(m => {
        const name = m.employee_name || m.name || '—';
        const ac   = accentFor(name);
        const rm   = ROLE_META[m.role] || ROLE_META.member;
        return (
          <div key={m.id}
            style={{ display:'flex', alignItems:'center', gap:'0.75rem',
                     padding:'0.625rem 0.875rem', background:'#fff',
                     border:'1px solid #E4E4E7', borderRadius:10 }}>
            {m.profile_photo ? (
              <img
                src={`http://localhost:8000/${m.profile_photo}`}
                alt={name}
                style={{ width:36, height:36, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:`2px solid ${ac}30` }}
                onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
              />
            ) : null}
            <div style={{ width:36, height:36, borderRadius:'50%', background:`${ac}18`, color:ac,
                          fontWeight:800, fontSize:'0.8rem', display: m.profile_photo ? 'none' : 'flex',
                          alignItems:'center', justifyContent:'center', flexShrink:0, border:`2px solid ${ac}30` }}>
              {initials(name)}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'0.85rem', fontWeight:700, color:'#111827' }}>{name}</div>
              {m.emp_code && <div style={{ fontSize:'0.7rem', color:'#9CA3AF', fontFamily:'monospace' }}>{m.emp_code}{m.department ? ` · ${m.department}` : ''}</div>}
            </div>
            {canManage ? (
              <select value={m.role} onChange={e => updateRole(m, e.target.value)}
                style={{ padding:'3px 8px', border:'1px solid #E4E4E7', borderRadius:7,
                         fontSize:'0.775rem', background:rm.bg, color:rm.color,
                         cursor:'pointer', fontWeight:700, outline:'none' }}>
                {MEMBER_ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_META[r]?.label || r.replace('_',' ')}</option>
                ))}
              </select>
            ) : (
              <span style={{ background:rm.bg, color:rm.color, padding:'3px 10px', borderRadius:9999,
                             fontSize:'0.72rem', fontWeight:700 }}>
                {rm.label}
              </span>
            )}
            {canManage && (
              <button onClick={() => removeMember(m)}
                style={{ padding:'3px 10px', border:'1px solid #FEE2E2', borderRadius:6,
                         background:'#fff', cursor:'pointer', fontSize:'0.75rem',
                         fontWeight:600, color:'#EF4444' }}
                onMouseEnter={e => e.currentTarget.style.background='#FEE2E2'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                Remove
              </button>
            )}
          </div>
        );
      })}

      {members.length === 0 && (
        <div style={{ textAlign:'center', padding:'2rem', color:'#9CA3AF' }}>
          <div style={{ fontSize:'1.75rem', marginBottom:'0.375rem' }}>👥</div>
          <p style={{ margin:0, fontSize:'0.85rem', fontWeight:600, color:'#374151' }}>No members yet</p>
          <p style={{ margin:'0.25rem 0 0', fontSize:'0.78rem' }}>Add team members to collaborate on this project.</p>
        </div>
      )}

      {/* Add member form */}
      {canManage && availableEmps.length > 0 && (
        <form onSubmit={addMember}
          style={{ padding:'1rem', background:'#F9FAFB', border:'1px dashed #E4E4E7',
                   borderRadius:10, display:'flex', gap:'0.75rem', flexWrap:'wrap', alignItems:'flex-end',
                   marginTop:'0.25rem' }}>
          <p style={{ margin:'0 0 0.5rem', fontSize:'0.78rem', fontWeight:700, color:'#6B7280', width:'100%' }}>
            Add Team Member
          </p>
          <div style={{ flex:1, minWidth:240 }}>
            <Field label="Employee">
              <EmployeeSearch
                employees={availableEmps}
                value={newEmpId}
                onChange={setNewEmpId}
              />
            </Field>
          </div>
          <div>
            <Field label="Role">
              <select value={newRole} onChange={e => setNewRole(e.target.value)}
                style={{ ...inputStyle, width:'auto' }}>
                {MEMBER_ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_META[r]?.label || r.replace('_',' ')}</option>
                ))}
              </select>
            </Field>
          </div>
          <button type="submit"
            style={{ padding:'0.45rem 1rem', border:'none', borderRadius:8,
                     background:theme.primary, color:'#fff', cursor:'pointer',
                     fontWeight:700, fontSize:'0.82rem', alignSelf:'flex-end', whiteSpace:'nowrap' }}>
            + Add Member
          </button>
        </form>
      )}
    </div>
  );
}

/* ─── FeaturesTab ────────────────────────────────────────────────── */
const FEATURE_DEFS = [
  {
    key:     'cycles',
    icon:    '🔄',
    title:   'Sprints',
    desc:    'Timebox work per project. One sprint can be 2 weeks, the next 1 week.',
    color:   '#8B5CF6',
  },
  {
    key:     'modules',
    icon:    '▦',
    title:   'Modules',
    desc:    'Organise work into sub-projects with dedicated leads and assignees.',
    color:   '#14B8A6',
  },
];

function ToggleSwitch({ on, onChange }) {
  return (
    <div onClick={() => onChange(!on)}
      style={{ width:42, height:24, borderRadius:12, background: on ? theme.primary : '#D1D5DB',
               cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
      <div style={{ position:'absolute', top:3, left: on ? 21 : 3, width:18, height:18,
                    borderRadius:'50%', background:'#fff', transition:'left 0.2s',
                    boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

function FeaturesTab({ project, onRefresh, user }) {
  const canModify = PROJECT_MODIFY_ROLES.includes(user?.role) ||
    (user?.emp_id != null && project?.manager_id != null &&
     Number(user.emp_id) === Number(project.manager_id));
  const raw      = project.features || {};
  const defaults = { cycles: true, modules: true };
  const [features, setFeatures] = useState({ ...defaults, ...raw });
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  const toggle = async (key, val) => {
    const next = { ...features, [key]: val };
    setFeatures(next);
    setSaving(true);
    try {
      await pm.updateProject(project.id, { features: next });
      onRefresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* revert on error */ setFeatures(features); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth:600, display:'flex', flexDirection:'column', gap:'0.75rem' }}>
      <p style={{ margin:'0 0 0.5rem', fontSize:'0.82rem', color:'#6B7280' }}>
        Toggle features on or off for this project.
      </p>
      {saved && (
        <span style={{ fontSize:'0.78rem', color:'#166534', fontWeight:600,
                       background:'#F0FDF4', padding:'4px 12px', borderRadius:9999,
                       border:'1px solid #BBF7D0', alignSelf:'flex-start' }}>
          ✓ Saved
        </span>
      )}
      {FEATURE_DEFS.map(f => (
        <div key={f.key}
          style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'1rem 1.125rem',
                   background:'#fff', border:'1px solid #E4E4E7', borderRadius:10,
                   borderLeft:`3px solid ${features[f.key] ? f.color : '#E4E4E7'}` }}>
          <div style={{ width:38, height:38, borderRadius:9, background:`${f.color}15`,
                        color:f.color, display:'flex', alignItems:'center',
                        justifyContent:'center', fontSize:'1.1rem', flexShrink:0 }}>
            {f.icon}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'0.875rem', fontWeight:700, color:'#111827' }}>{f.title}</div>
            <div style={{ fontSize:'0.75rem', color:'#9CA3AF', marginTop:2 }}>{f.desc}</div>
          </div>
          <ToggleSwitch on={!!features[f.key]}
            onChange={val => canModify && !saving && toggle(f.key, val)} />
        </div>
      ))}
    </div>
  );
}

/* ─── ProjectSettings (main) ────────────────────────────────────── */
const TABS = [
  { key:'general',  label:'General',  icon:'⚙' },
  { key:'features', label:'Features', icon:'◫' },
  { key:'states',   label:'States',   icon:'◉' },
  { key:'labels',   label:'Labels',   icon:'🏷' },
  { key:'members',  label:'Members',  icon:'👥' },
];

export default function ProjectSettings({ project, onRefresh }) {
  const { user }      = useAuth();
  const [tab, setTab] = useState('general');

  const active = TABS.find(t => t.key === tab) || TABS[0];

  return (
    <div style={{ display:'flex', height:'100%' }}>
      {/* Sidebar nav */}
      <div style={{ width:180, background:'#fff', borderRight:'1px solid #E4E4E7',
                    padding:'1rem 0', flexShrink:0 }}>
        <p style={{ margin:'0 0 0.75rem', padding:'0 1rem', fontSize:'0.7rem',
                    fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.05em' }}>
          Settings
        </p>
        {TABS.map(t => {
          const isActive = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.5rem',
                       padding:'0.5rem 1rem', border:'none', background: isActive ? '#F5F3FF' : 'transparent',
                       cursor:'pointer', fontSize:'0.82rem', fontWeight: isActive ? 700 : 400,
                       color: isActive ? theme.primary : '#374151', textAlign:'left',
                       borderLeft: isActive ? `3px solid ${theme.primary}` : '3px solid transparent',
                       transition:'all 0.1s' }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background='#F9FAFB'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background='transparent'; }}>
              <span style={{ fontSize:'0.75rem' }}>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ flex:1, overflowY:'auto', padding:'1.5rem 2rem' }}>
        <div style={{ marginBottom:'1.25rem' }}>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:'#111827' }}>
            {active.icon} {active.label}
          </h2>
          <p style={{ margin:'0.2rem 0 0', fontSize:'0.78rem', color:'#9CA3AF' }}>
            {tab === 'general'  && 'Update project name, description and status.'}
            {tab === 'features' && 'Toggle features on or off for this project.'}
            {tab === 'states'   && 'Manage workflow states for issues in this project.'}
            {tab === 'labels'   && 'Create labels to categorise and filter issues.'}
            {tab === 'members'  && 'Manage who has access to this project.'}
          </p>
        </div>

        {tab === 'general' && (
          <GeneralTab project={project} onRefresh={onRefresh} user={user} />
        )}
        {tab === 'features' && (
          <FeaturesTab project={project} onRefresh={onRefresh} user={user} />
        )}
        {tab === 'states' && (
          <StatesTab projectId={project.id} />
        )}
        {tab === 'labels' && (
          <LabelsTab projectId={project.id} />
        )}
        {tab === 'members' && (
          <MembersTab projectId={project.id} project={project} user={user} />
        )}
      </div>
    </div>
  );
}
